import { useState, type ReactNode } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { maskEmail } from '@/lib/mask-email';

/**
 * Screen-share-safe email display (operator request 2026-06-13): masked by
 * default everywhere, per-instance eye toggle to reveal. The toggle stops
 * propagation so it can live inside dropdown labels and table rows without
 * triggering row clicks or closing menus.
 */
export function RedactedEmail({ email, className }: { email: string; className?: string }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <span className={`inline-flex min-w-0 items-center gap-1 ${className ?? ''}`}>
      <span className="truncate">{revealed ? email : maskEmail(email)}</span>
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label={revealed ? 'Hide Email' : 'Show Email'}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setRevealed((v) => !v);
        }}
      >
        {revealed ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
      </Button>
    </span>
  );
}

// Conservative email matcher: a TLD of letters (no trailing dot) keeps the
// sentence-ending period out of the match (e.g. "...as a@b.com." → "a@b.com").
const EMAIL_PATTERN = /[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}/g;

/**
 * Renders arbitrary text with any embedded email address redacted via
 * <RedactedEmail /> (masked by default, eye toggle to reveal). Used where a
 * server-built string interpolates the user's email — e.g. the CLI
 * bootstrap-code warning copy — so the address never shows on screen or in a
 * screenshot until the owner clicks to reveal. Text with no email renders
 * verbatim.
 */
export function RedactedText({ text, className }: { text: string; className?: string }) {
  const parts: ReactNode[] = [];
  let last = 0;
  let key = 0;
  for (const match of text.matchAll(EMAIL_PATTERN)) {
    const start = match.index ?? 0;
    if (start > last) parts.push(text.slice(last, start));
    parts.push(<RedactedEmail key={key++} email={match[0]} />);
    last = start + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <span className={className}>{parts}</span>;
}

/**
 * Email <Input /> obscured like a password until the eye is clicked. The
 * value stays the real email (forms submit normally); only the glyphs are
 * hidden, so a screen recording of a profile or settings form leaks nothing.
 */
export function EmailInput({
  revealedType = 'email',
  ...props
}: React.ComponentProps<typeof Input> & { revealedType?: string }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="relative">
      <Input
        {...props}
        type={revealed ? revealedType : 'password'}
        autoComplete="off"
        className={`pr-9 ${props.className ?? ''}`}
      />
      <Button
        variant="ghost"
        size="icon-xs"
        aria-label={revealed ? 'Hide Email' : 'Show Email'}
        className="absolute top-1/2 right-1.5 -translate-y-1/2"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setRevealed((v) => !v);
        }}
      >
        {revealed ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
      </Button>
    </div>
  );
}
